const { parseRules, estimateUnitPrice, remainingStock, discountyRules, minimumBulkOffer } = await import(
  document.querySelector('printer-filament-addon').dataset.pricingModuleUrl
);
const quoteModuleUrl = document.querySelector('printer-filament-addon').dataset.quoteModuleUrl;
const quotes = quoteModuleUrl ? await import(quoteModuleUrl).catch(() => null) : null;

if (!customElements.get('printer-filament-addon')) {
  customElements.define('printer-filament-addon', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (!this.querySelector('[data-addon-product]')) { this.hidden = true; return; }
      this.initialized = true;
      this.panel = this.querySelector('[data-addon-panel]');
      this.trigger = this.querySelector('[data-addon-toggle]');
      this.status = this.querySelector('[data-addon-status]');
      this.inputs = this.querySelector('[data-addon-inputs]');
      this.viewAll = this.querySelector('[data-addon-view-all]');
      this.extraExpanded = false;
      this.isAccessory = this.dataset.addonKind === 'accessory';
      this.allowMultiple = this.dataset.allowMultiple === 'true';
      this.apiRules = [];
      this.apiReady = false;
      this.cartLines = [];
      this.cartReady = false;
      this.rules = [];
      try {
        this.rules = parseRules(this.querySelector('[data-addon-rules]').textContent);
      } catch (_) {
        this.dataset.pricingMode = 'catalog';
        this.ruleError = true;
      }
      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.onPickerOutside = event => {
        this.querySelectorAll('[data-addon-image-picker][open]').forEach(picker => {
          if (!picker.contains(event.target)) picker.open = false;
        });
      };
      document.addEventListener('pointerdown', this.onPickerOutside);
      document.addEventListener('focusin', this.onPickerOutside);
      this.onEditorSelect = event => {
        if (event.target === this && this.panel.hidden) this.togglePanel();
      };
      document.addEventListener('shopify:block:select', this.onEditorSelect);
      this.querySelectorAll('[data-addon-product]').forEach((product) => {
        this.updateQuantityState(product.querySelector('[data-addon-row]'));
      });
      this.onFocus = () => { this.refreshCart(); this.refreshDiscounts(); };
      window.addEventListener('focus', this.onFocus);
      document.addEventListener('cart:update', this.onFocus);
      this.refreshCart();
      this.refreshDiscounts();
      this.publishAnalytics('digitmakers:printer_filament_attachment_viewed', {
        parent_product_id: this.dataset.parentProductId || '',
        parent_product_handle: this.dataset.parentProductHandle || '',
        offer_product_count: this.querySelectorAll('[data-addon-product]').length
      });
    }

    disconnectedCallback() {
      quotes?.disconnectQuote(this);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      document.removeEventListener('pointerdown', this.onPickerOutside);
      document.removeEventListener('focusin', this.onPickerOutside);
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
      window.removeEventListener('focus', this.onFocus);
      document.removeEventListener('cart:update', this.onFocus);
      this.initialized = false;
    }

    handleClick(event) {
      const choice = event.target.closest('[data-addon-image-choice]');
      if (choice) {
        const row = choice.closest('[data-addon-row]');
        const select = row.querySelector('select');
        const option = [...select.options].find(option => option.value === choice.dataset.addonImageChoice);
        if (!option || option.disabled) return;
        select.value = option.value;
        row.querySelector('details').open = false;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        row.querySelector('summary').focus();
        return;
      }
      if (event.target.closest('.printer-filament-addon__product-link')) return;

      const productToggle = event.target.closest('[data-addon-product-select]');
      if (productToggle && !event.target.matches('[data-addon-product-checkbox]')) {
        const product = productToggle.closest('[data-addon-product]');
        const checkbox = productToggle.querySelector('[data-addon-product-checkbox]');
        if (!checkbox.checked) {
          checkbox.checked = true;
          this.toggleProduct(product, true);
        }
        return;
      }

      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;

      if (button.matches('[data-addon-toggle]')) return this.togglePanel();
      if (button.matches('[data-addon-view-all]')) return this.toggleExtraProducts();

      const product = button.closest('[data-addon-product]');
      if (button.matches('[data-addon-remove-colour]') && product) {
        if (!this.allowMultiple) return;
        const rows = product.querySelectorAll('[data-addon-row]');
        if (rows.length <= 1) return;
        rows[rows.length - 1].remove();
        this.renderSelection();
        if (button.hidden) product.querySelector('[data-addon-add-colour]').focus();
        return;
      }
      if (button.matches('[data-addon-add-colour]') && product) {
        if (!this.allowMultiple) return;
        const row = product.querySelector('[data-addon-row]').cloneNode(true);
        row.querySelector('[data-addon-image-picker]')?.remove();
        const select = row.querySelector('[data-addon-variant]');
        const used = new Set([...product.querySelectorAll('[data-addon-variant]')].map(select => select.value));
        const option = [...select.options].find(option => !used.has(option.value) && this.quantityLimit(option) > 0);
        if (!option) return;
        select.value = option.value;
        row.querySelector('[data-addon-qty]').value = 1;
        product.querySelector('[data-addon-rows]').append(row);
        this.renderSelection();
        select.focus();
        return;
      }
      if (button.matches('[data-addon-qty-minus], [data-addon-qty-plus]') && product) {
        const row = button.closest('[data-addon-row]');
        const input = row.querySelector('[data-addon-qty]');
        const checkbox = product.querySelector('[data-addon-product-checkbox]');
        if (button.matches('[data-addon-qty-minus]') && (+input.value || 1) <= 1) {
          if (product.querySelectorAll('[data-addon-row]').length > 1) {
            row.remove();
            product.querySelector('[data-addon-add-colour]').focus();
            return this.renderSelection();
          }
          checkbox.checked = false;
          return this.toggleProduct(product, false);
        }
        const delta = button.matches('[data-addon-qty-plus]') ? 1 : -1;
        input.value = (+input.value || 1) + delta;
        return this.syncProduct(product);
      }
    }

    handleChange(event) {
      const product = event.target.closest('[data-addon-product]');
      if (!product) return;

      if (event.target.matches('[data-addon-product-checkbox]')) {
        return this.toggleProduct(product, event.target.checked);
      }

      if (event.target.matches('[data-addon-qty]')) {
        return this.syncProduct(product);
      }

      if (!event.target.matches('[data-addon-variant]')) return;
      const option = event.target.selectedOptions[0];
      const image = product.querySelector('.printer-filament-addon__image');
      if (image && option.dataset.image) {
        image.removeAttribute('srcset');
        image.src = option.dataset.image;
      }
      this.updateQuantityState(event.target.closest('[data-addon-row]'));
      this.syncProduct(product);
    }

    handleInput(event) {
      if (!event.target.matches('[data-addon-qty]') || event.target.value === '') return;
      const product = event.target.closest('[data-addon-product]');
      const checkbox = product.querySelector('[data-addon-product-checkbox]');
      if (checkbox.checked) {
        this.syncProduct(product);
      } else {
        this.updateQuantityState(event.target.closest('[data-addon-row]'));
      }
    }

    togglePanel() {
      const willOpen = this.panel.hidden;
      this.panel.hidden = !willOpen;
      this.trigger.setAttribute('aria-expanded', String(willOpen));
      this.classList.toggle('is-open', willOpen);
      if (willOpen && this.dataset.pricingMode !== 'shopify_quote') { this.refreshCart(); this.refreshDiscounts(); }
      this.publishAnalytics(
        willOpen
          ? 'digitmakers:printer_filament_attachment_opened'
          : 'digitmakers:printer_filament_attachment_closed',
        { parent_product_id: this.dataset.parentProductId || '' }
      );
    }

    toggleProduct(product, selected) {
      const config = product.querySelector('[data-addon-config]');
      product.classList.toggle('is-selected', selected);
      config.hidden = !selected;

      if (selected) {
        this.syncProduct(product);
      } else {
        this.renderSelection();
        if (product.hasAttribute('data-addon-extra') && !this.extraExpanded) product.hidden = true;
      }
      const option = product.querySelector('[data-addon-variant]')?.selectedOptions?.[0];
      this.publishAnalytics('digitmakers:printer_filament_attachment_selection_changed', {
        parent_product_id: this.dataset.parentProductId || '',
        addon_product_id: product.dataset.addonProductId || '',
        addon_variant_id: option?.value || '',
        selected: Boolean(selected),
        quantity: selected ? [...product.querySelectorAll('[data-addon-qty]')].reduce((sum, input) => sum + Number(input.value), 0) : 0
      });
    }

    toggleExtraProducts() {
      this.extraExpanded = !this.extraExpanded;
      this.querySelectorAll('[data-addon-extra]').forEach((product) => {
        product.hidden = !this.extraExpanded && !product.classList.contains('is-selected');
      });
      this.viewAll.setAttribute('aria-expanded', String(this.extraExpanded));
      this.viewAll.classList.toggle('is-expanded', this.extraExpanded);
      this.viewAll.querySelector('[data-addon-view-all-label]').textContent = this.extraExpanded ? 'Show less' : 'View all';
    }

    syncProduct(product) {
      const checkbox = product.querySelector('[data-addon-product-checkbox]');
      if (!checkbox.checked) return;
      this.renderSelection();
    }

    quantityLimit(option) {
      const inCart = this.cartLines.reduce((sum, line) => sum + (String(line.id) === option.value ? line.quantity : 0), 0);
      return remainingStock(Number(option.dataset.quantityLimit), inCart);
    }

    async refreshDiscounts() {
      if (this.dataset.pricingMode !== 'discounty_best' || this.apiLoading || Date.now() - (this.apiUpdatedAt || 0) < 60000) return;
      const offered = new Set([...this.querySelectorAll('[data-addon-product]')].map(product => product.dataset.addonProductId));
      const ids = this.dataset.pricingMode === 'shopify_quote' ? [...offered].filter(id => /^\d+$/.test(id))
        : [...new Set((this.dataset.discountyProducts || '').split(',').filter(id => /^\d+$/.test(id) && offered.has(id)))];
      if (!ids.length) return;
      this.apiLoading = true;
      try {
        const shop = this.dataset.shopDomain;
        if (!/^[a-z0-9-]+\.myshopify\.com$/.test(shop)) throw new Error('Invalid shop');
        const response = await fetch('https://api-discounty.hengam.io/storefront/graphql', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(8000),
          body: JSON.stringify({ query: `query { appEmbed(shopifyDomain: "${shop}") { variantDiscounts(productIds: [${ids.join(',')}]) { productId variantId purchaseType discount { discountTiers discountType operandType scopeType sameLineStackEnabled } } } }` })
        });
        if (!response.ok) throw new Error('Discount API unavailable');
        const payload = await response.json();
        if (payload.errors || !Array.isArray(payload.data?.appEmbed?.variantDiscounts)) throw new Error('Invalid discount data');
        this.apiRules = discountyRules(payload.data.appEmbed.variantDiscounts, ids, this.dataset.currency);
        this.apiReady = true;
        this.apiUpdatedAt = Date.now();
      } catch (_) {
        this.apiRules = [];
        this.apiReady = false;
      } finally {
        this.apiLoading = false;
        if (this.isConnected) this.renderSelection();
      }
    }

    async refreshCart() {
      if (this.cartLoading) return;
      this.cartLoading = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        this.cartLines = cart.items.map(line => ({ id: String(line.variant_id), quantity: line.quantity, sellingPlan: Boolean(line.selling_plan_allocation) }));
        this.cartReady = true;
      } catch (_) {
        this.cartReady = false;
      } finally {
        this.cartLoading = false;
        if (this.isConnected) this.renderSelection();
      }
    }

    updateQuantityState(row) {
      const option = row.querySelector('[data-addon-variant]').selectedOptions[0];
      const limit = this.quantityLimit(option);
      const qtyInput = row.querySelector('[data-addon-qty]');
      const requested = Math.max(1, Number.parseInt(qtyInput.value, 10) || 1);
      const quantity = Math.min(limit, requested);
      if (quantity < requested && row.closest('[data-addon-product]').querySelector('[data-addon-product-checkbox]').checked) this.stockAdjusted = true;
      qtyInput.value = quantity;
      qtyInput.max = limit;
      qtyInput.min = limit > 0 ? 1 : 0;
      qtyInput.disabled = limit === 0;
      qtyInput.setAttribute('aria-label', `${option.dataset.title} quantity, maximum ${limit}`);

      const minusButton = row.querySelector('[data-addon-qty-minus]');
      minusButton.setAttribute('aria-label', quantity === 1 ? 'Remove item' : 'Decrease quantity');
      minusButton.title = quantity === 1 ? 'Remove item' : 'Decrease quantity';

      const plusButton = row.querySelector('[data-addon-qty-plus]');
      plusButton.disabled = quantity >= limit;
      plusButton.setAttribute('aria-label', quantity >= limit ? `Maximum available quantity: ${limit}` : 'Increase quantity');
      plusButton.title = quantity >= limit ? `Maximum available quantity: ${limit}` : 'Increase quantity';

      return quantity;
    }

    renderSelection() {
      const items = [];
      this.stockAdjusted = false;
      const products = [...this.querySelectorAll('[data-addon-product]')];
      const mode = this.dataset.pricingMode || 'catalog';
      const estimated = mode !== 'catalog';
      const rules = mode === 'discounty_best' ? [...this.rules, ...(this.cartReady ? this.apiRules : [])]
        : this.cartReady && ['rules', 'best'].includes(mode) ? this.rules : [];
      const fixedPercent = ['fixed', 'best', 'discounty_best'].includes(mode) ? Number(this.dataset.discountPercent) || 0 : 0;
      const format = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency', currency: this.dataset.currency || 'CAD', currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
      products.forEach(product => {
        const checked = product.querySelector('[data-addon-product-checkbox]').checked;
        const rows = [...product.querySelectorAll('[data-addon-row]')];
        const removeButton = product.querySelector('[data-addon-remove-colour]');
        removeButton.hidden = !this.allowMultiple || rows.length <= 1;
        const lastColour = rows[rows.length - 1].querySelector('select').selectedOptions[0].dataset.title;
        removeButton.setAttribute('aria-label', `Remove ${lastColour}`);
        removeButton.title = `Remove ${lastColour}`;
        const used = new Set(rows.map(row => row.querySelector('select').value));
        rows.forEach(row => {
          const select = row.querySelector('select');
          const option = select.selectedOptions[0];
          const quantity = this.updateQuantityState(row);
          [...select.options].forEach(choice => {
            choice.disabled = this.quantityLimit(choice) === 0 || (choice.value !== select.value && used.has(choice.value));
          });
          if (this.dataset.imagePicker === 'true') this.renderImagePicker(row, select);
          if (checked && quantity > 0) items.push({ id: option.value, quantity, price: Number(option.dataset.priceCents), row, product });
          row.querySelector('[data-addon-row-price]').textContent = `${format(Number(option.dataset.priceCents))} each`;
        });
        product.querySelector('[data-addon-add-colour]').disabled = [...rows[0].querySelector('select').options]
          .every(option => used.has(option.value) || this.quantityLimit(option) === 0);
      });
      const lines = [...this.cartLines, ...items];
      items.forEach(item => {
        item.unitPrice = estimateUnitPrice(item, lines, { rules, currency: this.dataset.currency, fixedPercent });
        item.row.querySelector('[data-addon-row-price]').textContent = `${format(item.unitPrice)} each`;
      });
      products.forEach(product => {
        const selected = items.filter(item => item.product === product);
        const checked = product.querySelector('[data-addon-product-checkbox]').checked;
        const base = checked ? selected.reduce((sum, item) => sum + item.quantity * item.price, 0)
          : Number(product.querySelector('select').selectedOptions[0].dataset.priceCents);
        const candidate = { id: product.querySelector('select').value, quantity: 1, price: base };
        const price = checked ? selected.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0)
          : estimateUnitPrice(candidate, [...lines, candidate], { rules, currency: this.dataset.currency, fixedPercent });
        const sale = product.querySelector('[data-addon-sale-price]');
        const compare = product.querySelector('[data-addon-compare-price]');
        sale.textContent = format(price);
        sale.classList.toggle('printer-filament-addon__sale-price', price < base);
        compare.textContent = format(base);
        compare.hidden = price >= base;
        product.querySelector('[data-addon-price]').setAttribute('aria-label', `${selected.length ? 'Selected subtotal' : 'Unit price'}: ${format(price)}`);
        const priceBlock = product.querySelector('[data-addon-price]');
        let bulkLabel = priceBlock.querySelector('[data-addon-bulk-from]');
        if (!bulkLabel) {
          bulkLabel = document.createElement('span');
          bulkLabel.dataset.addonBulkFrom = '';
          bulkLabel.className = 'printer-filament-addon__bulk-from';
          priceBlock.append(bulkLabel);
        }
        const option = product.querySelector('select').selectedOptions[0];
        const offer = ['discounty_best', 'shopify_quote'].includes(mode) && this.apiReady ? minimumBulkOffer(
          { id: option.value, price: Number(option.dataset.priceCents) },
          { rules: mode === 'shopify_quote' ? this.apiRules : rules, currency: this.dataset.currency, fixedPercent }
        ) : null;
        product.dataset.bulkMinimum = offer && offer.minimum <= this.quantityLimit(option) ? String(offer.minimum) : '';
        bulkLabel.hidden = !offer || mode === 'shopify_quote';
        bulkLabel.textContent = offer ? `From ${format(offer.price)} each \u00b7 ${offer.minimum}+` : '';
        priceBlock.setAttribute('aria-label', `${selected.length ? 'Selected subtotal' : 'Unit price'}: ${format(price)}${offer ? `. Bulk unit price from ${format(offer.price)} when buying ${offer.minimum} or more` : ''}`);
      });
      const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      const formattedSubtotal = new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        currency: this.dataset.currency || 'CAD'
      }).format(subtotal / 100);

      this.status.textContent = quantity
        ? `+${formattedSubtotal}`
        : 'Optional';
      this.status.setAttribute('aria-label', quantity ? `Additional subtotal: ${formattedSubtotal}` : 'Optional items');
      this.trigger.classList.toggle('is-selected', quantity > 0);

      this.inputs.innerHTML = items.map(item => `
        <input type="hidden" name="buy_with_${this.isAccessory ? 'accessory' : 'filament'}_${this.dataset.instanceId}_${item.id}" value="${item.id}" data-quantity="${item.quantity}">
      `).join('');
      const notice = this.querySelector('[data-addon-notice]');
      notice.textContent = this.stockAdjusted ? 'Quantity adjusted to available stock.'
        : this.ruleError ? 'Final pricing is calculated in your cart.'
        : mode === 'discounty_best' && !this.apiReady ? 'Bulk pricing is currently unavailable. Final pricing is calculated in your cart.'
        : mode === 'discounty_best' || estimated ? ''
        : quantity && !this.isAccessory ? 'Bulk discounts are calculated in your cart.' : '';
      notice.hidden = !notice.textContent;
      if (mode === 'shopify_quote') {
        if (quotes) quotes.scheduleQuote(this);
        else {
          this.dataset.quoteState = 'error';
          this.querySelectorAll('[data-quote-badge], [data-quote-info], [data-addon-compare-price]').forEach(node => node.hidden = true);
          this.querySelectorAll('[data-addon-sale-price], [data-addon-row-price]').forEach(node => node.textContent = 'See cart');
          this.status.textContent = 'See cart';
        }
      }
    }

    renderImagePicker(row, select) {
      let picker = row.querySelector('[data-addon-image-picker]');
      if (!picker) {
        picker = document.createElement('details');
        picker.dataset.addonImagePicker = '';
        picker.className = 'printer-filament-addon__image-picker';
        picker.append(document.createElement('summary'), document.createElement('div'));
        picker.lastChild.className = 'printer-filament-addon__image-options';
        picker.addEventListener('keydown', event => {
          if (event.key === 'Escape') {
            event.preventDefault();
            picker.open = false;
            picker.firstChild.focus();
          }
          if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            picker.open = true;
            const choices = [...picker.querySelectorAll('button:not(:disabled)')];
            const index = choices.indexOf(document.activeElement);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1
              : event.key === 'ArrowDown' ? Math.min(index + 1, choices.length - 1)
              : index < 0 ? choices.length - 1 : Math.max(index - 1, 0);
            choices[next]?.focus();
          }
        });
        select.after(picker);
        select.hidden = true;
      }
      const content = option => {
        const fragment = document.createDocumentFragment();
        if (option.dataset.image) {
          const image = document.createElement('img');
          image.src = option.dataset.image;
          image.alt = '';
          image.width = 32;
          image.height = 32;
          image.loading = 'lazy';
          fragment.append(image);
        }
        const text = document.createElement('span');
        text.textContent = option.dataset.title;
        fragment.append(text);
        return fragment;
      };
      picker.firstChild.replaceChildren(content(select.selectedOptions[0]));
      picker.firstChild.setAttribute('aria-label', `${select.getAttribute('aria-label') || 'Variant'}: ${select.selectedOptions[0].dataset.title}`);
      picker.lastChild.replaceChildren(...[...select.options].map(option => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.addonImageChoice = option.value;
        button.disabled = option.disabled;
        button.setAttribute('aria-pressed', String(option.selected));
        button.append(content(option));
        return button;
      }));
    }

    publishAnalytics(name, payload) {
      if (this.isAccessory) name = name.replace('printer_filament_attachment', 'printer_accessory_attachment');
      document.dispatchEvent(new CustomEvent(name, { detail: payload }));
      if (!window.Shopify?.analytics?.publish) return;
      try {
        window.Shopify.analytics.publish(name, {
          ...payload,
          attribution_version: 'preview-1'
        });
      } catch (_) {
        // Analytics must never block product configuration.
      }
    }

  });
}
